import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

export interface IFormProps extends IBaseElementProps {
	useBackground?: boolean;
	isCentered?: boolean;
	/**
	 * Form width is set to 540px
	 */
	compactForm?: boolean;
	/**
	 * Sets alignment for all consent checkboxes
	 */
	alignCheckboxes?: 'left' | 'center' | 'right'
}

/**
 * Form wrapper. Place Gravity Form inside to style the form.
 * @block
 * @requiresHooks
 */

const Form: React.FC<IFormProps> = ({
	className,
	classNames = [],
	children,
	useBackground = false,
	isCentered = false,
	compactForm = false,
	alignCheckboxes = 'center',
}) => {
	const ref = useRef<HTMLDivElement>(null);

	// Adds focus classes to fields wrappers
	useEffect(() => {
		let fields;
		let fieldContainers;

		const handleFocus = (event: FocusEvent) => {
			(event.target as HTMLElement)
				.closest('.gfield')
				.classList.add('input-focused');
		};
		const handleBlur = (event: FocusEvent) => {
			(event.target as HTMLElement)
				.closest('.gfield')
				.classList.remove('input-focused');
		};

		const handleAODA = (event: any) => {
			const recaptchaTextarea = ref.current?.querySelectorAll(
				'.g-recaptcha-response',
			) as NodeListOf<HTMLElement>;

			const recaptchaIframes = ref.current?.querySelectorAll(
				'.ginput_recaptcha iframe',
			) as NodeListOf<HTMLElement>;

			const hiddenIframes = ref.current?.querySelectorAll(
				'iframe[style^="display: none"]',
			) as NodeListOf<HTMLElement>;

			recaptchaTextarea?.forEach((textarea) => {
				textarea.setAttribute('aria-label', 'ReCaptcha Response');
			});

			recaptchaIframes?.forEach((iframe) => {
				iframe.setAttribute('title', 'ReCaptcha frame');
			});

			hiddenIframes?.forEach((iframe) => {
				iframe.setAttribute('title', 'Hidden iframe');
			});
		};

		const handleChange = (event: any) => {
			const target = event.target as HTMLInputElement;
			const { value } = target;
			const wrapper = target.closest('.gfield');
			if (value) {
				wrapper?.classList.add('input-filled');
			} else {
				wrapper?.classList.remove('input-filled');
			}
		};
		const setListeners = () => {
			fields = ref.current?.querySelectorAll(
				'input, textarea, select',
			) as NodeListOf<HTMLInputElement>;
			fields.forEach((field) => {
				field.addEventListener('focus', handleFocus);
				field.addEventListener('blur', handleBlur);
				field.addEventListener('change', handleChange);
				if(field.type === 'checkbox') {
					field.addEventListener('click', handleAODA);
				}

				handleChange({
					target: field,
				});
			});
		};
		// fixes the label top distance for form fields that have descriptions above the input.
		const fixLabelHeight = (container) => {
			const label = container.querySelector(
				'.gfield_label',
			) as HTMLLabelElement;
			const input = container.querySelector(
				'select, input:not([type=\'checkbox\']):not(.gform_hidden)',
			) as HTMLDivElement;
			if (!input) {
				return;
			}

			if (!label.classList.contains('gfield_label_before_complex')) {
				label.style.top = `${input?.offsetTop}px`;
			}
		};

		const setLabelListener = () => {
			fieldContainers = ref.current?.querySelectorAll(
				'.gfield.field_description_above',
			) as NodeListOf<HTMLElement>;
			fieldContainers.forEach((container) => {
				fixLabelHeight(container);
			});
		};

		const setChosenSearchInputParameters = () => {
			const chosenSearchInput = ref.current?.querySelectorAll(
				'.chosen-search-input',
			) as NodeListOf<HTMLInputElement>;
			chosenSearchInput.forEach((input) => {
				input.setAttribute('aria-label', 'Search');
			});
		};

		// MutationObserver to watch for GForm changes after initial page load
		const observer = new MutationObserver((mutationList, observer) => {
			for (const mutation of mutationList) {
				if (mutation.type === 'attributes') { // will trigger when inline style changes
					// @ts-ignore
					const classNames = mutation.target?.classList;
					if (classNames.contains('field_description_above')) {
						fixLabelHeight(mutation.target);
					}
				} else if (mutation.type === 'childList') { // will trigger when children are added/removed
					setListeners();
					setLabelListener();
				}
			}
		});

		const { document } = window;

		// listeners need to be added once children loaded
		document.addEventListener('DOMContentLoaded', setListeners);
		// fix label height once form is rendered
		window.addEventListener('load', setLabelListener);
		window.addEventListener('load', setChosenSearchInputParameters);
		// this is to handle multipage forms and forms that use conditional logic
		observer.observe(ref.current, { attributes: true, childList: true, subtree: true });

		return () => {
			observer.disconnect();
			window.removeEventListener('load', setLabelListener);
			window.removeEventListener('load', setChosenSearchInputParameters);
			document.removeEventListener('DOMContentLoaded', setListeners);

			fields?.forEach((field) => {
				field.removeEventListener('focus', handleFocus);
				field.removeEventListener('blur', handleBlur);
				field.removeEventListener('change', handleChange);
				field.removeEventListener('click', handleAODA);
			});
		};
	}, []);

	return (
		<div
			className={
				className ||
				cn(
					'form-wrapper',
					useBackground && 'form-wrapper--background',
					isCentered && 'form-wrapper--centered',
					compactForm && 'form-wrapper--compact',
					`form-wrapper--align-checkbox-${alignCheckboxes}`,
					...classNames,
				)
			}
			ref={ref}
		>
			{children}
		</div>
	);
};

export default Form;
